import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

/**
 * Onglet admin « Emailing » — façon Strategiegains.
 * Agrège les emails déjà en base (comptes, clients, contacts CRM), permet d'en
 * sélectionner, puis d'ouvrir Gmail avec les adresses en Cci (envoi depuis la
 * boîte de l'admin) ou de les copier pour n'importe quel webmail.
 * Aucune collecte cachée : uniquement des emails donnés volontairement.
 */

const SOURCE_LABELS = {
    compte: { label: 'Compte', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
    client: { label: 'Client', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
    contact: { label: 'Contact', className: 'bg-purple-50 text-purple-700 ring-purple-600/20' },
};

const DEFAULT_SUBJECT = 'Des nouvelles de Mirum Games 🎮';
const DEFAULT_MESSAGE =
    "Bonjour,\n\nMerci de faire partie de l'aventure Mirum Games !\n\n" +
    "[Écrivez votre message ici]\n\n" +
    'À très vite,\nL’équipe Mirum Games\nwww.mirumgames.com';

const EmailingPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selected, setSelected] = useState(() => new Set());
    const [search, setSearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [subject, setSubject] = useState(DEFAULT_SUBJECT);
    const [message, setMessage] = useState(DEFAULT_MESSAGE);
    const [copyMsg, setCopyMsg] = useState('');

    useEffect(() => {
        api.get('/admin/recipients')
            .then((res) => setData(res.data))
            .catch((err) => {
                console.error('Error fetching recipients:', err);
                setError("Impossible de charger la liste des contacts.");
            })
            .finally(() => setLoading(false));
    }, []);

    const recipients = data?.recipients || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return recipients.filter((r) => {
            if (sourceFilter !== 'all' && !r.sources.includes(sourceFilter)) return false;
            if (!q) return true;
            return r.email.toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q);
        });
    }, [recipients, search, sourceFilter]);

    const selectedEmails = useMemo(
        () => recipients.filter((r) => selected.has(r.email)).map((r) => r.email),
        [recipients, selected]
    );
    const selectedCount = selectedEmails.length;

    const toggle = (email) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(email)) next.delete(email);
            else next.add(email);
            return next;
        });
    };

    const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.email));

    const toggleAllFiltered = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) filtered.forEach((r) => next.delete(r.email));
            else filtered.forEach((r) => next.add(r.email));
            return next;
        });
    };

    const gmailHref = () =>
        `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(selectedEmails.join(','))}` +
        `&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;

    const copyEmails = async () => {
        const text = selectedEmails.join(', ');
        try {
            if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
            else throw new Error('no clipboard api');
            setCopyMsg(`✅ ${selectedCount} adresse${selectedCount > 1 ? 's' : ''} copiée${selectedCount > 1 ? 's' : ''} — collez-les dans le champ Cci de votre messagerie.`);
        } catch {
            setCopyMsg(`Copie auto impossible. Adresses (Cci) : ${text}`);
        }
        setTimeout(() => setCopyMsg(''), 8000);
    };

    if (loading) return <div className="dashboard-loading">Chargement des contacts...</div>;
    if (error) return <div className="dashboard-error">{error}</div>;
    if (!data) return null;

    const counts = data.counts || { comptes: 0, clients: 0, contacts: 0 };

    return (
        <div className="space-y-6">
            {/* Bandeau d'explication */}
            <div className="rounded-xl bg-white p-6 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900">✉️ Emailing</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Sélectionne des contacts, puis ouvre Gmail avec leurs adresses en <strong>Cci</strong>
                    {' '}(copie cachée : les destinataires ne se voient pas entre eux). Tu envoies depuis ta propre boîte.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                    <span className="rounded-lg bg-blue-50 px-3 py-1.5 font-medium text-blue-700">{counts.comptes} compte{counts.comptes > 1 ? 's' : ''}</span>
                    <span className="rounded-lg bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700">{counts.clients} client{counts.clients > 1 ? 's' : ''}</span>
                    <span className="rounded-lg bg-purple-50 px-3 py-1.5 font-medium text-purple-700">{counts.contacts} contact{counts.contacts > 1 ? 's' : ''}</span>
                    <span className="rounded-lg bg-gray-100 px-3 py-1.5 font-medium text-gray-700">{data.total} adresse{data.total > 1 ? 's' : ''} unique{data.total > 1 ? 's' : ''}</span>
                </div>
            </div>

            {/* Rédaction du message */}
            <div className="rounded-xl bg-white p-6 shadow-lg">
                <h3 className="mb-4 text-lg font-bold text-gray-800">Ton message</h3>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Objet</label>
                <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="mb-4 block w-full rounded-lg border-0 bg-gray-50 px-4 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-200 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-teal-600"
                />
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Message</label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={8}
                    className="block w-full resize-y rounded-lg border-0 bg-gray-50 px-4 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-200 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-teal-600"
                />
            </div>

            {/* Barre d'action (collante) */}
            <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-lg ring-1 ring-gray-100">
                <span className="text-sm font-bold text-gray-800">
                    {selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}
                </span>
                <a
                    href={selectedCount === 0 ? undefined : gmailHref()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { if (selectedCount === 0) e.preventDefault(); }}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-md transition-all ${selectedCount === 0 ? 'cursor-not-allowed bg-gray-300' : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:shadow-lg'}`}
                >
                    📧 Ouvrir dans Gmail
                </a>
                <button
                    onClick={copyEmails}
                    disabled={selectedCount === 0}
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    📋 Copier les adresses
                </button>
                {selectedCount > 0 && (
                    <button
                        onClick={() => { setSelected(new Set()); setCopyMsg(''); }}
                        className="text-sm text-gray-500 underline hover:text-gray-700"
                    >
                        Tout désélectionner
                    </button>
                )}
                {copyMsg && <span className="text-sm text-emerald-700">{copyMsg}</span>}
            </div>

            {/* Filtres + liste */}
            <div className="rounded-xl bg-white p-6 shadow-lg">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <input
                        type="text"
                        placeholder="Rechercher un nom ou un email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 rounded-lg border-0 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-teal-600"
                    />
                    <select
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}
                        className="rounded-lg border-0 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-teal-600"
                    >
                        <option value="all">Toutes les sources</option>
                        <option value="compte">Comptes inscrits</option>
                        <option value="client">Clients</option>
                        <option value="contact">Contacts CRM</option>
                    </select>
                </div>

                {filtered.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-400">
                        {recipients.length === 0
                            ? "Aucun email en base pour l'instant. Les adresses arriveront avec les inscriptions, commandes et messages de contact."
                            : 'Aucun contact ne correspond à ce filtre.'}
                    </p>
                ) : (
                    <div className="overflow-hidden rounded-lg ring-1 ring-gray-200">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="w-10 px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected}
                                            onChange={toggleAllFiltered}
                                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                            title="Tout sélectionner"
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Nom</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Source</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {filtered.map((r) => (
                                    <tr
                                        key={r.email}
                                        className={`cursor-pointer transition-colors hover:bg-gray-50 ${selected.has(r.email) ? 'bg-teal-50/50' : ''}`}
                                        onClick={() => toggle(r.email)}
                                    >
                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selected.has(r.email)}
                                                onChange={() => toggle(r.email)}
                                                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-800">{r.name || <span className="italic text-gray-400">—</span>}</td>
                                        <td className="px-4 py-3 text-gray-600">{r.email}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {r.sources.map((s) => {
                                                    const meta = SOURCE_LABELS[s] || { label: s, className: 'bg-gray-50 text-gray-600 ring-gray-500/20' };
                                                    return (
                                                        <span key={s} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.className}`}>
                                                            {meta.label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <p className="text-xs text-gray-400">
                Rappel RGPD : n'écris qu'à des personnes qui t'ont laissé leur email (inscription, commande, contact).
                Pour un envoi commercial, propose toujours un moyen de se désabonner (« réponds STOP pour ne plus être contacté »).
                Les adresses partent en <strong>Cci</strong> : jamais visibles entre destinataires.
            </p>
        </div>
    );
};

export default EmailingPanel;
